import { useEffect, useState } from 'react';
import { Icon } from './material';
import { Sheet, Soft } from './widgets';

/**
 * «Добавить на экран «Домой»» — the coach iOS makes necessary.
 *
 * Until the passenger app is on the App Store, this web app *is* the iPhone app
 * (ADR 0006). Safari gives it no `beforeinstallprompt`, no install button and no way
 * for a page to ask: the only route is Share → «На экран «Домой»», and a passenger
 * who does not know that has a bookmark instead of an app.
 *
 * It is worth the strip of screen for a second reason, which is less obvious and
 * worse. Safari's tracking prevention deletes script-writable storage — localStorage
 * included, which is where the session lives — after seven days without the site
 * being opened. A passenger who orders a taxi twice a month is signed out every time
 * in a plain tab. Installed to the home screen, that eviction does not apply. So this
 * is not a nag about engagement; it is the difference between staying signed in and
 * not.
 *
 * Shown once, dismissible for good, and only where it is both true and actionable:
 * a real iPhone or iPad, in Safari, not already installed.
 */

const DISMISSED = 'limuzin.customer.install-dismissed';

/** An iPhone or an iPad, in whatever browser. */
function isIos(ua: string = navigator.userAgent): boolean {
  // iPadOS 13+ reports itself as a Mac. A Mac with a touchscreen is an iPad.
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isIosSafari(): boolean {
  // Chrome and Firefox on iOS are Safari underneath, but their share sheets have no
  // «На экран «Домой»» — telling somebody in Chrome to look for it is a dead end.
  return isIos() && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);
}

export function isInstalled(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari's own flag, which predates `display-mode` and is what actually answers
    // on the iOS versions that most need this.
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

export function InstallCoach() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISSED) === '1';
    } catch {
      // A private window. Nothing was saved, so nothing was dismissed.
    }
    if (!dismissed && isIosSafari() && !isInstalled()) setShow(true);
  }, []);

  if (!show) return null;

  return (
    <div className="install">
      <span aria-hidden="true" style={{ fontSize: 18, lineHeight: '20px' }}>
        📲
      </span>
      <div className="grow">
        <div style={{ font: '700 13.5px/1.35 Manrope', color: 'var(--ink)' }}>
          Добавьте «Лимузин» на экран «Домой»
        </div>
        <p className="body" style={{ margin: '4px 0 0', fontSize: 14 }}>
          <b>Поделиться</b> → <b>«На экран „Домой“»</b>. Откроется сразу и не будет
          выкидывать из аккаунта.
        </p>
        <div style={{ marginTop: 8 }}>
          <Soft
            tone="quiet"
            onClick={() => {
              setShow(false);
              try {
                localStorage.setItem(DISMISSED, '1');
              } catch {
                // Nothing to remember it with. It will ask again, which is the
                // gentler failure of the two.
              }
            }}
          >
            Не сейчас
          </Soft>
        </div>
      </div>
    </div>
  );
}

export type Guide = 'iphone' | 'chrome' | 'yandex' | 'firefox' | 'other';

/**
 * Which steps fit the browser this page is open in. Every Chromium on Android says
 * `Chrome/`, so Chrome is what says it and names nothing else; Samsung Internet and
 * the rest each hide the item somewhere of their own. `YaBrowser/` is Яндекс Браузер
 * alone: the Яндекс app with Алиса says `YaSearchBrowser/` and has menus of its own.
 */
export function guessGuide(ua: string = navigator.userAgent): Guide {
  if (isIos(ua)) return 'iphone';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/YaBrowser\//.test(ua)) return 'yandex';
  if (
    /Chrome\//.test(ua) &&
    !/SamsungBrowser|YaBrowser|YaSearchBrowser|OPR\/|EdgA\/|MiuiBrowser|HeyTapBrowser|UCBrowser|Vivaldi|; wv\)/.test(ua)
  ) {
    return 'chrome';
  }
  // A computer, where nothing here applies, lands on the commonest phone browser.
  return /Android/.test(ua) ? 'other' : 'chrome';
}

const BROWSERS = [
  ['chrome', 'Chrome'],
  ['yandex', 'Яндекс'],
  ['firefox', 'Firefox'],
  ['other', 'Другой'],
] as const;

/** What the steps are for, said over them while nobody has had to choose. */
const WHERE: Record<Guide, string> = {
  iphone: 'На iPhone',
  chrome: 'В Chrome',
  yandex: 'В Яндекс Браузере',
  firefox: 'В Firefox',
  other: 'В вашем браузере',
};

/** ⋮, drawn beside its name as it looks on the phone. */
const Dots = () => (
  <span className="glyph">
    <Icon name="more_vert" size={16} />
  </span>
);

/**
 * «Нужна помощь?» on the sign-in screen: how to put «Лимузин» on the phone's home
 * screen, in words for somebody who has never done it.
 *
 * The coach above appears only in Safari on an iPhone, and only after signing in;
 * this is for everybody, before. On Android the item is in a different place in
 * every browser, so each has its own steps. Only the steps for the phone and browser
 * this page is open in are shown; the tabs for the others wait behind «Другой
 * телефон или браузер?», because the guess can be wrong — «Версия для ПК» hides an
 * Android, and somebody may be reading this for a relative. On a computer there is
 * nothing to guess from, so the tabs are open from the start.
 *
 * Every name in quotes is the browser's own. Firefox's and Яндекс Браузер's were read
 * off their menus on a phone (Firefox 158, Яндекс 26.10, October 2026). Яндекс has two
 * menus, and the item is in ⋮ in the address bar at the foot of the screen, not in ☰
 * below it. Chrome's are from Chromium's Russian strings
 * (`IDS_MENU_INSTALL_WEBAPP`, `IDS_APP_BANNER_INSTALL`): a site with a manifest, as
 * this one is, gets «Установить приложение» rather than a shortcut.
 */
export function InstallHelp({ onClose }: { onClose: () => void }) {
  const [guide, setGuide] = useState<Guide>(() => guessGuide());
  const [browser, setBrowser] = useState<Exclude<Guide, 'iphone'>>(
    guide === 'iphone' ? 'chrome' : guide,
  );
  const phone = guide === 'iphone' ? 'iphone' : 'android';
  const [choosing, setChoosing] = useState(() => !isIos() && !/Android/.test(navigator.userAgent));

  return (
    <Sheet
      title="Как поставить «Лимузин» на телефон"
      hint="На экране телефона появится значок, как у обычного приложения. Нажали — и можно заказывать."
      onClose={onClose}
    >
      {choosing ? (
        <>
          <div className="auth-tabs" role="tablist" style={{ marginTop: 0 }}>
            {(['iphone', 'android'] as const).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={key === phone}
                className={key === phone ? 'on' : ''}
                onClick={() => setGuide(key === 'iphone' ? 'iphone' : browser)}
              >
                {key === 'iphone' ? 'iPhone' : 'Android'}
              </button>
            ))}
          </div>

          {phone === 'android' && (
            <div className="install-browsers">
              <span className="label">Браузер</span>
              <div className="auth-tabs" role="tablist">
                {BROWSERS.map(([key, name]) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={key === guide}
                    className={key === guide ? 'on' : ''}
                    onClick={() => {
                      setBrowser(key);
                      setGuide(key);
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <span className="label install-where">{WHERE[guide]}</span>
      )}

      {guide === 'iphone' && (
        <ol className="install-steps">
          {/* The one browser where the steps below are known to be there. */}
          {isIos() && !isIosSafari() && <li>Откройте этот сайт в Safari.</li>}
          <li>
            Нажмите кнопку «Поделиться»{' '}
            <span className="glyph">
              <Icon name="ios_share" size={16} />
            </span>{' '}
            — квадрат со стрелкой вверх. Если её не видно, сначала нажмите{' '}
            <span className="glyph">
              <Icon name="more_horiz" size={16} />
            </span>
            .
          </li>
          <li>Пролистайте список вниз и выберите «На экран „Домой“».</li>
          <li>Нажмите «Добавить» вверху справа.</li>
        </ol>
      )}
      {guide === 'chrome' && (
        <ol className="install-steps">
          <li>
            Нажмите <Dots /> — три точки рядом с адресом сайта.
          </li>
          <li>Выберите «Установить приложение».</li>
          <li>Нажмите «Установить».</li>
        </ol>
      )}
      {guide === 'yandex' && (
        <ol className="install-steps">
          <li>
            Нажмите <Dots /> — три точки в адресной строке внизу экрана.
          </li>
          <li>Пролистайте меню вниз и выберите «Добавить ярлык на рабочий стол».</li>
          <li>Нажмите «Добавить».</li>
        </ol>
      )}
      {guide === 'firefox' && (
        <ol className="install-steps">
          <li>
            Нажмите <Dots /> — три точки рядом с адресом сайта.
          </li>
          <li>Нажмите «Больше».</li>
          <li>Пролистайте меню вниз и выберите «Добавить приложение на домашний экран».</li>
          <li>Нажмите «Добавить».</li>
        </ol>
      )}
      {guide === 'other' && (
        <ol className="install-steps">
          <li>
            Откройте меню браузера — обычно это <Dots /> или ☰ рядом с адресом сайта.
          </li>
          <li>Поищите пункт со словами «главный экран», «домашний экран» или «Установить».</li>
          <li>
            Нет такого пункта? Откройте этот сайт в Chrome — он есть почти на каждом
            телефоне с Android — и следуйте шагам для Chrome.
          </li>
        </ol>
      )}

      <p className="body" style={{ margin: '16px 0 0', fontSize: 14.5 }}>
        Готово: значок «Лимузин» — на экране телефона. Открывайте приложение с него.
      </p>

      {!choosing && (
        <p style={{ textAlign: 'center', margin: '10px 0 0' }}>
          <button type="button" className="linkish" onClick={() => setChoosing(true)}>
            Другой телефон или браузер?
          </button>
        </p>
      )}
    </Sheet>
  );
}
